import React, { useEffect, useState, useRef } from 'react';
import logoColor from './logo.png';
import logoWhite from './logo_white.png';

// ---- enquiry form validation rules (shared, no React state needed) ----
const validators = {
    name: (v) => /^[a-zA-Z0-9 ]{1,50}$/.test(v),
    phone: (v) => /^\d{10}$/.test(v),
    email: (v) => /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(v),
    message: (v) => v.trim().length > 0 && v.trim().length <= 500,
};

function toggleValidationClass(input, isValid) {
    if (!input) return;
    input.classList.toggle('is-valid', isValid);
    input.classList.toggle('is-invalid', !isValid);
}

export default function Home() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const formRef = useRef(null);
    const nameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);
    const messageRef = useRef(null);

    // header shadow on scroll
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // scroll-reveal + count-up animation, respects prefers-reduced-motion
    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        function animateCount(el) {
            const target = parseInt(el.getAttribute('data-count'), 10);
            const suffix = el.getAttribute('data-suffix') || '';
            if (reduceMotion) {
                el.textContent = target + suffix;
                return;
            }
            const duration = 1100;
            const start = performance.now();
            function step(now) {
                const p = Math.min((now - start) / duration, 1);
                const eased = 1 - Math.pow(1 - p, 3);
                el.textContent = Math.round(eased * target) + suffix;
                if (p < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
        }
        document.querySelectorAll('[data-count]').forEach((el) => {
            setTimeout(() => animateCount(el), 550);
        });

        if (reduceMotion) {
            document.querySelectorAll('.reveal, .reveal-scale').forEach((el) => el.classList.add('in'));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in');
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
        );
        document.querySelectorAll('.reveal, .reveal-scale').forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);

    // ---- enquiry form: live validation + filtering on each field ----
    useEffect(() => {
        const cnameField = nameRef.current;
        const cphoneField = phoneRef.current;
        const cemailField = emailRef.current;
        const cmessageField = messageRef.current;
        if (!cnameField || !cphoneField || !cemailField || !cmessageField) return;

        function setupValidation(input, validator, filterRegex, maxLength) {
            const handler = function () {
                if (filterRegex) this.value = this.value.replace(filterRegex, '');
                if (maxLength) this.value = this.value.slice(0, maxLength);
                toggleValidationClass(this, validator(this.value));
            };
            input.addEventListener('input', handler);
            return () => input.removeEventListener('input', handler);
        }

        const cleanups = [
            setupValidation(cnameField, validators.name, /[^a-zA-Z0-9 ]/g, 50),
            setupValidation(cphoneField, validators.phone, /\D/g, 10),
            setupValidation(cemailField, validators.email, /[^\w.@+\-]/g, null),
            setupValidation(cmessageField, validators.message, null, 500),
        ];

        return () => cleanups.forEach((fn) => fn());
    }, []);

    // ---- enquiry form: submit handler ----
    async function handleEnquirySubmit(e) {
        e.preventDefault();

        const cnameField = nameRef.current;
        const cphoneField = phoneRef.current;
        const cemailField = emailRef.current;
        const cmessageField = messageRef.current;

        const name = cnameField.value.trim();
        const phone = cphoneField.value.trim();
        const email = cemailField.value.trim();
        const message = cmessageField.value.trim();

        const isValid =
            validators.name(name) &&
            validators.phone(phone) &&
            validators.email(email) &&
            validators.message(message);

        toggleValidationClass(cnameField, validators.name(name));
        toggleValidationClass(cphoneField, validators.phone(phone));
        toggleValidationClass(cemailField, validators.email(email));
        toggleValidationClass(cmessageField, validators.message(message));

        if (!isValid) return;

        const data = { name, mobile: phone, email, message };

        setSubmitting(true);
        try {
            const response = await fetch('https://api.keyconnect.in/api/v1/orgadmin/generateEnquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            const result = await response.json();

            if (response.ok) {
                alert('Request sent successfully!');
                formRef.current.reset();
                formRef.current
                    .querySelectorAll('input, textarea')
                    .forEach((i) => i.classList.remove('is-valid', 'is-invalid'));
            } else {
                alert('Submission failed: ' + (result.message || 'Please try again later.'));
            }
        } catch (err) {
            alert('An error occurred. Please check your connection.');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <a
                href="https://wa.me/916363114836"
                className="wa-float"
                aria-label="Chat on WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
            >
                <svg viewBox="0 0 448 512">
                    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.7z" />
                </svg>
            </a>

            <header className={scrolled ? 'scrolled' : ''}>
                <div className="wrap navrow">
                    <a href="#top" className="brand">
                        <img src={logoColor} alt="KeyConnect" />
                    </a>
                    <nav className="links">
                        <a href="#top">Home</a>
                        <a href="#about">About</a>
                        <a href="#features">Features</a>
                        <a href="#pipeline">How it works</a>
                        <a href="#contact">Contact</a>
                    </nav>
                    <div className="nav-cta">
                        <a href="https://app.keyconnect.in/" className="login-link">Login</a>
                        <a href="#contact" className="btn btn-grad">Request Demo</a>
                        <button className="burger" aria-label="Open menu" onClick={() => setMenuOpen((v) => !v)}>☰</button>
                    </div>
                </div>
                <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
                    <a href="#top" onClick={() => setMenuOpen(false)}>Home</a>
                    <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
                    <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
                    <a href="#pipeline" onClick={() => setMenuOpen(false)}>How it works</a>
                    <a href="#contact" onClick={() => setMenuOpen(false)}>Contact</a>
                    <a href="https://app.keyconnect.in/">Login</a>
                </div>
            </header>

            <main id="top">
                {/* HERO */}
                <section className="hero">
                    <div className="hero-bg">
                        <div className="blob blob1"></div>
                        <div className="blob blob2"></div>
                    </div>
                    <div className="wrap hero-grid">
                        <div>
                            <span className="eyebrow hero-anim d1">Real Estate CRM · Built for India</span>
                            <h1 className="hero-anim d2">
                                Turn every enquiry into a <span className="grad-text">key handover.</span>
                            </h1>
                            <p className="lead hero-anim d3">
                                KeyConnect is a cloud-based lead management platform built for real estate teams. Capture
                                enquiries the moment they arrive, follow up automatically and see exactly which deals are
                                closest to closing.
                            </p>
                            <div className="hero-ctas hero-anim d4">
                                <a href="#contact" className="btn btn-grad">Request a Demo</a>
                                <a href="#features" className="btn btn-outline">Explore Features</a>
                            </div>
                            <div className="hero-stats hero-anim d5">
                                <div>
                                    <div className="num" data-count="6" data-suffix="+">0</div>
                                    <div className="lbl">Lead sources unified</div>
                                </div>
                                <div>
                                    <div className="num" data-count="100" data-suffix="%">0</div>
                                    <div className="lbl">Real-time capture</div>
                                </div>
                                <div>
                                    <div className="num">24/7</div>
                                    <div className="lbl">Automated follow-ups</div>
                                </div>
                            </div>
                        </div>

                        <div className="key-wrap hero-anim d6">
                            <div className="key-card">
                                <div className="key-card-top">
                                    <span className="mono">LEAD PIPELINE</span>
                                    <span className="mono live-dot">LIVE</span>
                                </div>
                                <svg className="key-svg" viewBox="0 0 460 220" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <defs>
                                        <linearGradient id="g1" x1="24" y1="64" x2="420" y2="156" gradientUnits="userSpaceOnUse">
                                            <stop offset="0" stopColor="#6C5CE7" />
                                            <stop offset="0.4" stopColor="#4A8FE6" />
                                            <stop offset="0.72" stopColor="#12D6C0" />
                                            <stop offset="1" stopColor="#00E6B8" />
                                        </linearGradient>
                                    </defs>
                                    <circle cx="70" cy="110" r="46" stroke="#EDECF9" strokeWidth="10" />
                                    <circle cx="70" cy="110" r="46" stroke="url(#g1)" strokeWidth="3.5" />
                                    <rect x="112" y="102" width="300" height="16" rx="8" fill="#F1F0FA" />
                                    <circle className="signal" cx="112" cy="110" r="5" fill="#12D6C0" />
                                    <g className="key-node n1">
                                        <rect x="150" y="118" width="10" height="26" fill="#6C5CE7" opacity="0.35" />
                                        <circle cx="155" cy="102" r="9" fill="#fff" stroke="#6C5CE7" strokeWidth="2.5" />
                                    </g>
                                    <g className="key-node n2">
                                        <rect x="220" y="118" width="10" height="34" fill="#4A8FE6" opacity="0.35" />
                                        <circle cx="225" cy="102" r="9" fill="#fff" stroke="#4A8FE6" strokeWidth="2.5" />
                                    </g>
                                    <g className="key-node n3">
                                        <rect x="300" y="118" width="10" height="20" fill="#12D6C0" opacity="0.4" />
                                        <circle cx="305" cy="102" r="9" fill="#fff" stroke="#12D6C0" strokeWidth="2.5" />
                                    </g>
                                    <g className="key-node n4">
                                        <rect x="378" y="118" width="10" height="40" fill="#00E6B8" opacity="0.55" />
                                        <circle cx="383" cy="102" r="10" fill="#00E6B8" stroke="#00E6B8" strokeWidth="2.5" />
                                    </g>
                                </svg>
                                <div className="stage-caps">
                                    <span>INQUIRY</span><span>SITE VISIT</span><span>BOOKING</span><span>CLOSURE</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* TRUST STRIP */}
                <section className="trust">
                    <div className="wrap">
                        <span className="trust-label reveal">Leads flow in from</span>
                        <div className="trust-logos reveal">
                            <span>99acres</span><span>MagicBricks</span><span>Meta</span>
                            <span>Website Forms</span><span>Phone Calls</span><span>Walk-ins</span>
                        </div>
                    </div>
                </section>

                {/* ABOUT / WHY */}
                <section className="why section-pad" id="about">
                    <div className="wrap why-grid">
                        <div className="why-copy reveal">
                            <span className="eyebrow">About KeyConnect</span>
                            <h2>Built so your team spends time selling, not searching for leads.</h2>
                            <p>
                                We built KeyConnect because great sales happen when a team has the right tools — not more
                                spreadsheets. It's a cloud-based SaaS platform that helps developers, brokers and agencies
                                capture, track, manage and follow up with leads in real time, whether you're a lean start-up
                                or a scaling enterprise.
                            </p>
                            <p>KeyConnect is secure, built on modern architecture and scales with you as your team and listings grow.</p>
                            <a href="#contact" className="btn btn-grad">Talk to Us</a>
                        </div>
                        <div className="why-list">
                            <div className="why-item reveal">
                                <div className="why-num">01</div>
                                <div><h4>Centralized Lead Tracking</h4><p>Every enquiry — from website forms, calls, referrals or campaigns — lands in one unified dashboard.</p></div>
                            </div>
                            <div className="why-item reveal">
                                <div className="why-num">02</div>
                                <div><h4>Automated Follow-ups</h4><p>Scheduled reminders and personalised sequences make sure a lead never goes cold from a missed callback.</p></div>
                            </div>
                            <div className="why-item reveal">
                                <div className="why-num">03</div>
                                <div><h4>Real-Time Analytics</h4><p>Visual dashboards show lead status, sales performance and conversion rate as they happen.</p></div>
                            </div>
                            <div className="why-item reveal">
                                <div className="why-num">04</div>
                                <div><h4>Multi-User Access</h4><p>Assign leads to agents, create user groups, define roles and track progress across the whole team.</p></div>
                            </div>
                            <div className="why-item reveal">
                                <div className="why-num">05</div>
                                <div><h4>Secure &amp; Scalable</h4><p>Enterprise-grade security on modern architecture, built to grow alongside your business.</p></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FEATURES */}
                <section className="section-pad" id="features">
                    <div className="wrap">
                        <div className="section-head reveal">
                            <span className="eyebrow">Key Features</span>
                            <h2>Everything a real estate sales team needs, in one CRM.</h2>
                            <p>From the first enquiry to the final signature, KeyConnect gives your team the tools to capture, prioritise and close.</p>
                        </div>
                        <div className="feat-grid">
                            <div className="feat-card reveal">
                                <div className="feat-index">01 / Capture</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M3 8l9 6 9-6M4 6h16v12H4z" /></svg></div>
                                <h3>Multi-Channel Lead Capture</h3>
                                <p>Centralize leads from website forms, landing pages, social campaigns, 99acres, MagicBricks, WhatsApp, calls and offline events — organized automatically in real time.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">02 / Pipeline</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M4 6h16M4 12h10M4 18h6" /></svg></div>
                                <h3>Pipeline Management</h3>
                                <p>Move leads through customizable stages — inquiry, site visit, booking, closure — and spot bottlenecks with real-time insight, across residential, commercial or rental deals.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">03 / Reminders</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M12 6v6l4 2M12 22a9 9 0 100-18 9 9 0 000 18z" /></svg></div>
                                <h3>Automated Follow-Ups</h3>
                                <p>Timely reminders trigger off lead behaviour, pipeline stage and agent activity — from post-visit callbacks to document submission nudges.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">04 / Integration</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M4 4h16v16H4z" /><path d="M4 9h16M9 20V9" /></svg></div>
                                <h3>Email &amp; SMS Integration</h3>
                                <p>Send personalised messages, property updates and reminders individually or in bulk and track delivery and response in real time.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">05 / Scoring</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" /></svg></div>
                                <h3>Lead Prioritisation &amp; Scoring</h3>
                                <p>Every lead gets a dynamic score based on behaviour, engagement, budget and timeline, so your team follows up hot prospects first.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">06 / Filters</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M4 5h16M7 12h10M10 19h4" /></svg></div>
                                <h3>Custom Tags &amp; Filters</h3>
                                <p>Segment leads, properties and activity by interest, project, budget or location, then search and act on the right group instantly.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">07 / Performance</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M4 20V10M12 20V4M20 20v-7" /></svg></div>
                                <h3>Agent Performance Tracking</h3>
                                <p>Track response time, follow-up consistency, site visits and closures per agent, with exportable reports to reward top performers.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">08 / Tracking</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2" /></svg></div>
                                <h3>Campaign Tracking</h3>
                                <p>Track leads from ads, email and listing portals with UTM and source tagging and see which campaigns actually drive qualified leads.</p>
                            </div>
                            <div className="feat-card reveal">
                                <div className="feat-index">09 / Access</div>
                                <div className="feat-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" /></svg></div>
                                <h3>Role-Based Access Control</h3>
                                <p>Give admins, managers and agents exactly the permissions they need, keeping pricing, commissions and lead sources protected.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* PIPELINE / HOW IT WORKS */}
                <section className="pipeline" id="pipeline">
                    <div className="wrap">
                        <div className="section-head reveal" style={{ marginBottom: 0 }}>
                            <span className="eyebrow">How It Works</span>
                            <h2>From first enquiry to key handover, four stages.</h2>
                            <p>Every lead moves through the same clear pipeline, so nothing slips through and every deal is visible.</p>
                        </div>
                        <div className="pipeline-track">
                            <div className="p-stage reveal"><div className="p-dot">01</div><h4>Inquiry</h4><p>Captured automatically the moment a lead comes in, from any source.</p></div>
                            <div className="p-stage reveal"><div className="p-dot">02</div><h4>Site Visit</h4><p>Scheduled, tracked and followed up on without manual chasing.</p></div>
                            <div className="p-stage reveal"><div className="p-dot">03</div><h4>Booking</h4><p>Documents, reminders and agent notes stay attached to the deal.</p></div>
                            <div className="p-stage reveal"><div className="p-dot">04</div><h4>Closure</h4><p>Deal closed, performance logged, insights fed back to your team.</p></div>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIAL */}
                <section className="quote-sec">
                    <div className="wrap">
                        <div className="quote-mark reveal-scale">"</div>
                        <blockquote className="reveal">
                            An all-in-one CRM designed specifically for real estate professionals — capturing leads from
                            listing sites, walk-ins, social ads and WhatsApp, all in one place.
                        </blockquote>
                        <div className="quote-attr reveal">THE CRM YOUR REAL ESTATE BUSINESS HAS BEEN WAITING FOR</div>
                    </div>
                </section>

                {/* DEMO / CONTACT */}
                <section className="demo" id="contact">
                    <div className="wrap demo-grid">
                        <div className="demo-info reveal">
                            <span className="eyebrow">Get In Touch</span>
                            <h2>See KeyConnect on your own leads.</h2>
                            <p>Book a walkthrough with our team or reach out directly — we typically respond within a business day.</p>
                            <div className="contact-list">
                                <a href="tel:+916363114836">
                                    <span className="ico"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006.3 6.3l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" /></svg></span>
                                    +91 63631 14836
                                </a>
                                <a href="mailto:support@keyconnect.in">
                                    <span className="ico"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M4 4h16v16H4z" /><path d="M4 6l8 7 8-7" /></svg></span>
                                    support@keyconnect.in
                                </a>
                                <a
                                    href="https://www.google.com/maps/search/?api=1&query=%231278%2C+1st+Floor%2C+25th+Main%2C+40th+Cross+Rd%2C+9th+Block%2C+Jayanagar%2C+Bengaluru%2C+Karnataka+560041"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="ico"><svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M12 22s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg></span>
                                    #1278, 1st Floor, 25th Main, 40th Cross Rd, 9th Block, Jayanagar, Bengaluru, Karnataka 560041
                                </a>
                            </div>
                            <a
                                className="store-badge"
                                href="https://play.google.com/store/apps/details?id=com.octal.keyconnect"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Get KeyConnect on Google Play"
                                style={{ display: 'inline-block', width: '160px' }}
                            >
                                <svg viewBox="0 0 165 48" xmlns="http://www.w3.org/2000/svg" width="160" height="50">
                                    <rect x="0.5" y="0.5" width="164" height="47" rx="7" fill="#000" stroke="#8b8b8b" strokeWidth="0.6" />
                                    <g transform="translate(13,12)">
                                        <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zM13.792 12l3.606-3.583-13.99-7.94c-.324-.187-.63-.25-.897-.207L13.792 12zm0 0L2.499 23.06c.276.045.593-.02.926-.214l13.985-7.926L13.792 12z" fill="#fff" />
                                    </g>
                                    <text x="46" y="20" fill="#fff" fontFamily="Arial, Helvetica, sans-serif" fontSize="7.5" letterSpacing="0.6">GET IT ON</text>
                                    <text x="45" y="37" fill="#fff" fontFamily="Arial, Helvetica, sans-serif" fontSize="15.5" fontWeight="600">Google Play</text>
                                </svg>
                            </a>
                        </div>

                        <div className="demo-card reveal">
                            <span className="eyebrow">Schedule Demo</span>
                            <h3>Your Smart Lead Management CRM</h3>
                            <form id="contactform" className="demo-form" ref={formRef} onSubmit={handleEnquirySubmit} noValidate>
                                <div className="field">
                                    <label htmlFor="cname">Full Name</label>
                                    <input id="cname" name="cname" maxLength={50} type="text" placeholder="Your name" ref={nameRef} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="cphone">Phone</label>
                                    <input id="cphone" name="cphone" type="tel" placeholder="10-digit mobile number" ref={phoneRef} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="cmail">Email</label>
                                    <input id="cmail" name="cmail" type="email" placeholder="you@company.com" ref={emailRef} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="cmessage">What are you looking for?</label>
                                    <textarea id="cmessage" name="cmessage" rows="3" placeholder="Tell us about your team and lead volume" ref={messageRef}></textarea>
                                </div>
                                <label className="consent">
                                    <input type="checkbox" required style={{ marginRight: 6 }} />
                                    I consent to KeyConnect contacting me via Email / SMS / Call / WhatsApp.
                                </label>
                                <button type="submit" className="btn btn-grad" disabled={submitting}>
                                    {submitting ? 'Sending…' : 'Submit Request'}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            </main>

            <footer>
                <div className="wrap">
                    <div className="foot-grid">
                        <div className="foot-brand">
                            <img src={logoWhite} alt="KeyConnect" />
                            <p>An all-in-one CRM designed for real estate professionals — real-time dashboards, mobile access and seamless integrations to help you close deals faster. Capture leads the moment they come in from listing portals or social ads, automate follow-ups so no lead falls through the cracks and track every deal through your pipeline from first contact to final closing — all from a single, easy-to-use platform.</p>
                        </div>
                        <div>
                            <h5>Navigate</h5>
                            <ul>
                                <li><a href="#top">Home</a></li>
                                <li><a href="#about">About</a></li>
                                <li><a href="#features">Features</a></li>
                                <li><a href="#pipeline">How it works</a></li>
                                <li><a href="#contact">Contact</a></li>
                            </ul>
                        </div>
                        <div>
                            <h5>Policies</h5>
                            <ul>
                                <li><a href="/terms">Terms &amp; Conditions</a></li>
                                <li><a href="/privacy-policy">Privacy Policy</a></li>
                                <li><a href="/cookies-policy">Cookies Policy</a></li>
                                <li><a href="/payment-policy">Payment Policy</a></li>

                                <li><a href="refund-policy">Refund Policy</a></li>
                                <li><a href="cancellation-policy">Cancellation Policy</a></li>

                            </ul>
                        </div>
                        <div>
                            <h5>Contact</h5>
                            <ul>
                                <li><a href="mailto:support@keyconnect.in">support@keyconnect.in</a></li>
                                <li><a href="tel:+916363114836">+91 63631 14836</a></li>
                                <li><a href="https://wa.me/916363114836">Chat on WhatsApp</a></li>
                                <li><a href="https://app.keyconnect.in/">Login to KeyConnect</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="foot-bottom">
                        <span>© 2026 KeyConnect. All rights reserved.</span>
                        <span></span>
                    </div>
                </div>
            </footer>
        </>
    );
}